import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

// Design guide: Fluent tokens only (light / dark / high contrast), visible focus, dashed empty state,
// Skeleton while loading, row actions on the right, state never conveyed by color alone.
const focusRing = {
  outlineStyle: 'solid',
  outlineWidth: tokens.strokeWidthThick,
  outlineColor: tokens.colorStrokeFocus2,
  outlineOffset: '2px',
};

export const useFilePickerDialogStyles = makeStyles({
  surface: {
    width: 'min(880px, calc(100vw - 32px))',
    maxWidth: 'min(880px, calc(100vw - 32px))',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    minHeight: '320px',
  },
  topRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalS,
  },
  breadcrumb: {
    minWidth: 0,
  },
  search: {
    width: '260px',
    maxWidth: '100%',
  },
  list: {
    listStyleType: 'none',
    ...shorthands.margin(0),
    ...shorthands.padding(0),
    maxHeight: '46vh',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    minHeight: '40px',
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalM),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke3),
    ':last-child': {
      ...shorthands.borderBottom('0', 'none', 'transparent'),
    },
  },
  rowSelected: {
    // Guía, grilla: fila seleccionada con fondo primario tenue y barra azul a la izquierda.
    backgroundColor: 'var(--ichtys-row-selected-bg)',
    boxShadow: 'inset 4px 0 0 var(--ichtys-row-selected-bar)',
  },
  folderButton: {
    flexGrow: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    fontWeight: tokens.fontWeightSemibold,
    ':focus-visible': focusRing,
  },
  folderIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  fileCheckbox: {
    flexGrow: 1,
    minWidth: 0,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    textAlign: 'center',
    ...shorthands.border(tokens.strokeWidthThin, 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    color: tokens.colorNeutralForeground2,
  },
  emptyTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  summary: {
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: 'tabular-nums',
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    listStyleType: 'none',
    ...shorthands.margin(0),
    ...shorthands.padding(0),
  },
  resultRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
  },
  resultName: {
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: 'anywhere',
  },
  resultMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
  },
});
